<template>
    <div>
        <MTB_DropdownModal v-if="dropdown_open" @close-modal="dropdown_open = false" :websocket="websocket"/>
        <div>
            <!--Patient Info and selection window-->
            <div class="mtb-presentation-patients" id="patient_overview">
                <div class="mtb-presentation-patient-data">
                    <p v-if="current_patient">
                        <span style="font-size:1.25rem">{{`${current_patient.id}`}}, </span>
                        {{current_patient.sex ? current_patient.sex : "unknown sex"}}, 
                        {{current_patient.birthdate ? `*${current_patient.birthdate} (X yrs.)` : "unknown d.o.b."}} 
                    </p>
                    <p v-if="current_patient">
                        Cancer Type: {{current_patient.cancer_type ? current_patient.cancer_type : "unknown"}},
                        {{current_patient.mtb_registration_date ? current_patient.mtb_registration_date : "unknown registration date"}}, 
                        {{current_patient.mtb_conference_date ? current_patient.mtb_conference_date : "unknown conference date"}}
                    </p>
                    <p v-if="!current_patient"><span style="font-size:24px">No patient found</span></p>
                    <p v-if="!current_patient">Please verify the patient register</p>       
                </div>
                <div class="mtb-presentation-buttons">
                    <v-btn @click="toPrevPatient(), toFirstSlide()">Prev. patient</v-btn>
                    <v-btn @click="toNextPatient(), toFirstSlide()">Next patient</v-btn>
                </div>
                <div class="mtb-presentation-patient-selection">
                        <label>Choose group:</label>
                        <select v-model="presentation_mode" class="patient-dropdown" @change="changePresentationMode()">
                            <option value="selected" v-if="Object.keys(selected_patients).length > 0">Only selected patients</option>
                            <option value="all">All patients</option>
                        </select>
                        <label>Choose patient:</label>
                        <select v-if="presentation_mode == 'selected'" class="patient-dropdown" v-model="presentation_selected_patient" @change="updateCurrentPatient(), toFirstSlide()">
                            <option v-for="patient in Object.keys(selected_patients)">{{patient}}</option>
                        </select>
                        <select v-if="presentation_mode == 'all'" class="patient-dropdown" v-model="presentation_selected_patient" @change="updateCurrentPatient(), toFirstSlide()">
                            <option v-for="patient in Object.keys(all_patients)">{{patient}}</option>
                        </select>
                </div>
                <div class="mtb-presentation-icons">
                    <v-icon size="large" color="var(--default-blue)" icon="mdi-web" @click="toggleWebsocket" :class="{'active-reg': participating_in_websocket}"/>
                    <v-icon size="large" color="var(--default-blue)" icon="mdi-cog" @click="dropdown_open = !dropdown_open" :class="{'active-cog': dropdown_open}"/>
                </div>
            </div>
            <!--Vue Carousel with different slides-->
            <div class="mtb-presentation-progress">
                <div class="mtb-presentation-progress-item" :class="{ 'mtb-presentation-progress-item-active': i == slide}" v-for="i in Object.keys(all_slides)"
                 @click="switchToSlide(i)">
                    {{all_slides[i].title}}
                </div>
            </div>
            <v-carousel v-model="slide" class="mtb-presentation-carousel" hide-delimiters id="mtb-slides" @update:modelValue="onFlipSlide()">
                <v-carousel-item v-for="i in Object.keys(all_slides)" :key="i" :value="i">
                    <v-sheet height="100%" width="100%" tile>
                        <MTB_Slide_PatientData v-if="all_slides[i].id == 'patient_data'"/>
                        <MTB_Slide_VariantData v-if="all_slides[i].id == 'variants'"/>
                        <MTB_Slide_MedResults v-if="all_slides[i].id == 'recommendations'"/>
                        <div v-if="!Object.keys(all_slides).includes(i)">No slide here.</div>
                    </v-sheet>
                </v-carousel-item>
            </v-carousel>
        </div>
    </div>
</template>

<script setup lang="js">
import { ref, onMounted, computed, watch } from 'vue';
import MTB_Slide_PatientData from '@/components/presentation_components/MTB_Slide_PatientData.vue';
import MTB_Slide_VariantData from '@/components/presentation_components/MTB_Slide_VariantData.vue';
import id_requests from "@/methods/id_requests";
import { useStore } from 'vuex';
import MTB_Slide_MedResults from '@/components/presentation_components/MTB_Slide_MedResults.vue';
import MTB_DropdownModal from '@/components/presentation_components/MTB_DropdownModal.vue';

const store = useStore();
const selected_patients = computed(() => store.state.presentation_selected_patients);
const all_patients = computed(() => store.state.presentation_all_patients);
const current_patient = computed(() => store.state.presentation_current_patient);
const presentation_mode = computed(() => store.state.presentation_mode);
const config = computed(() => store.state.presentation_config);
const participating_in_websocket = computed(() => props.websocket != null);
const all_slides = computed(() => store.state.presentation_slides);
const slide = computed({
    get() {
        return store.state.presentation_slide;
    },
    set(slide) {
        store.dispatch('setPresentationSlide', slide);
    }
});
const current_mtb = computed(() => store.state.current_mtb);
const patient_id_list = computed(() => store.state.id_list);

/****************************************************************************************
 *  Main methods                                                                        *
 ***************************************************************************************/

/**
 * When the slides view is mounted, the following steps are performed:
 * 1. All patients are the loaded and cached in the store with their IDs as strings (as backup)
 * 2. Presentation mode (by default 'selected') is set to 'all' if no patients were selected. --> currently unused
 * 3. If there is no current patient, the first one from the list is chosen and the dropdown (top right) is updated accordingly.
 * 4. The view scrolls so that the top of the viewport aligns with the top of the presentation.
 */
onMounted(async () => {
    await id_requests.getMTBData(current_mtb.value);
    store.dispatch("setIDList", store.state.mtb_data[current_mtb.value]);
    fetchPatients();
    
    if (!current_patient.value) pickFirstPatient();
    if (current_patient.value) presentation_selected_patient.value = current_patient.value.id;
    
    let overview_element = document.getElementById('patient_overview');
    overview_element?.scrollIntoView({alignToTop: 'true'});
});

/**
 * @emit toggle-websocket - Toggles a websocket connection on or off (requires WS prop to be passed).
 */
const emit = defineEmits(['toggle-websocket']);

/**
 * @prop websocket - A websocket that can be passed down from App.vue. If no websocket connection exists, it is null.
 */
const props = defineProps({
    websocket: WebSocket|null,
})

/**
 * Loads patient data (for the current MTB) from store and filters them.
 * Then saves it in a dedicated presentation_all_patients variable.
 */
const fetchPatients = () => {
    var all_patients_raw = store.getters.idList;
    if (all_patients_raw !== undefined) {
        Object.keys(all_patients_raw).map(key => {
            delete all_patients_raw[key]['id'];
            all_patients_raw[key]['id'] = String(key);
        })
        store.dispatch('setPresentationAllPatients', all_patients_raw);
    }
    if (Object.keys(all_patients.value).length && current_patient.value === null) pickFirstPatient();
}

/**
 * Sets a current patient if the user selects one from the dropdown menu.
 */
const updateCurrentPatient = () => {
    setPatientByID(presentation_selected_patient.value);
}

/**
 * Goes to the next patient (chronologically) in the list.
 */
const toNextPatient = () => {
    setPatientByIndex(1);
}

/**
 * Goes to the previous patient (chronologically) in the list.
 */
const toPrevPatient = () => {
    setPatientByIndex(-1);
}

/**
 * Switches to a specific slide.
 * @param {String|Number} slide_index - The slide to switch to, given by the index (e.g. 0 or "0")
 */
const switchToSlide = (slide_index) => {
    slide.value = slide_index.toString();
    props.websocket?.send(JSON.stringify({codeword: 'im-showing', showing: all_slides.value[slide.value].title}))
}

/**
 * Sets the current patient based on a starting position (patient_index) and an added or subtracted offset.
 * This means either a hard index can be set (offset 0, patient_index n) or the lists can be shuffled (offset -1/+1)
 * Index will either be applied to the all_patients list or the selected_patients list if the app is in selected mode.
 * @param {number} offset - Will be added to the given patient_index (which, by default, will be the current patient's index)
 * @param {object} patient_list - Patient list to use
 * @param {number} patient_index - Starting point in the list, optional (otherwise currently selected patient's index is used)
 * @param {boolean} send - Dictates whether to pass on the patient to other clients
 */
const setPatientByIndex = (offset, patient_index, send=true) => {
    store.dispatch('setPresentationPatientByIndex',
     {offset: offset, patient_index: patient_index});
    if (participating_in_websocket.value && send) {
        props?.websocket?.send(JSON.stringify({codeword: 'adapt-current-patient-id', id: current_patient.value?.id}));
    } 
}

/**
 * Sets the current patient based on the provided ID.
 * @param {string} patient_id - ID of the patient to set as current patient
 * @param {object} patient_list - Patient list to use
 * @param {boolean} send - Dictates whether to pass on the patient to other clients
 */
const setPatientByID = (patient_id, send=true) => {
    store.dispatch('setPresentationPatientByID',
    {patient_id: patient_id,});
    if (participating_in_websocket.value && send) {
        props?.websocket?.send(JSON.stringify({codeword: 'adapt-current-patient-id', id: current_patient.value?.id}));
    } 
}

/**
 * Simply picks the first patient from the corresponding list.
 */
const pickFirstPatient = () => {
    setPatientByIndex(0, 0, false);
}

/**
 * Informs the websocket server about the slide that is shown after the user switches to a new slide.
 */
const onFlipSlide = () => {
    props.websocket?.send(JSON.stringify({codeword: 'im-showing', showing: all_slides.value[slide.value].title}))
}

/**
 * Goes to the first slide, or slide 0.
 * Will only do something if the user has activated "firstPageOnSwitch" option.
 * Slide must be given as a string here, or it just won't work.
 */
const toFirstSlide = () => {
    if (!config.value.firstPageOnSwitch) return;
    store.dispatch('setPresentationSlide', "0");
}

/**
 * Returns the right ref given the presentation mode.
 */
const patientListByMode = () => {
    return (presentation_mode.value == 'all' ? all_patients.value : selected_patients.value);
}

/**
 * Should contain anything that happens when the presentation mode (all/selected) is switched.
 * Currently that is nothing. :-)
 */
const changePresentationMode = () => {
    return;
}

/****************************************************************************************
 *  This part covers the WebSocket API messaging methods.                               *
 *  This includes sending, receiving and listening to messages on either of the         *
 *  shared channels.                                                                    *
 ***************************************************************************************/

/**
 * Toggles the websocket connection on or off.
 */
const toggleWebsocket = () => {
    emit('toggle-websocket', {showing: all_slides.value[slide.value].title})
}

/****************************************************************************************
 *  Refs for rendering and configuration of the slides                                  *
 ***************************************************************************************/

// Refs that are used for rendering relevant information, and browser-browser communication
const presentation_selected_patient = ref('');
const dropdown_open = ref(false);
const slide_manual_set = ref(false);

/**
 * This watch block is responsible for "flipping" through the patients if the user
 * clicks through the carousel, either to the left or right.
 * This can be deactivated by setting 'nextPatientOnCarousel' to false, which is also the default.
 */
watch(() => slide.value, (new_value, old_value) => {
    var overview_element = document.getElementById('patient_overview');
    overview_element?.scrollIntoView({alignToTop: 'true'});
    if (!config.value.nextPatientOnCarousel || slide_manual_set.value) return;
    // Right arrow:
    if (old_value == Object.keys(all_slides.value).length -1 && new_value == 0) {
        toNextPatient();
    }
    // Left Arrow:
    if (new_value == Object.keys(all_slides.value).length -1 && old_value == 0) {
        toPrevPatient();
    }
    props.websocket?.send(JSON.stringify({codeword: 'im-showing', showing: all_slides.value[slide.value].title}))

});

/**
 * This watch block simply updates the selection dropdown on top of the presentation view.
 * It is triggered if a new patient is selected.
 */
watch(() => current_patient.value, () => {
    if (current_patient.value === null) return;
    presentation_selected_patient.value = current_patient.value?.id;
});

/**
 * Makes sure that selected patients mode is only active if there are actually selected patients.
 * Also picks the first list patient if there is currently no patient.
 */
watch(() => presentation_mode.value, (value) => {
    if (value == 'selected' && Object.keys(selected_patients.value).length == 0) {
        store.dispatch('setPresentationMode', 'all');
    }
    if (!current_patient.value && Object.keys(patientListByMode()).length != 0) pickFirstPatient();
})

/**
 * If new patients are loaded,the current selection is cleared and the list is reloaded.
 */
watch(() => patient_id_list.value, () => {
    store.dispatch('clearPresentationSelectedPatients');
    fetchPatients();
})
</script>

<style>

.mtb-presentation-patients {
    height: 100%;
    margin-left: 16px;
    margin-right: 32px;
    display: grid;
    grid-template-columns: repeat(4, minmax(fit-content, max-content));
    grid-auto-flow: column;
    column-gap: 32px;
    position: relative;
    max-height: 100px;
}

.mtb-presentation-carousel {
    height: 50rem !important;
}

.mtb-presentation-progress {
    grid-template-columns: repeat(4, 1fr);
    max-height: 3rem;
    display: grid;
    grid-auto-flow: row;
    background-color: #d3d3d3;
    align-content: center;
}

.mtb-presentation-progress-item {
    max-height: 3rem;
    color: white;
    font-size: larger;
    font-weight: bold;
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;
    transition: 0.5s ease-out;
    cursor: pointer;
}

.mtb-presentation-progress-item-active {
    color: var(--default-blue);
    background-color: var(--light-blue);
}

.mtb-presentation-icons {
    height: 100%;
    display: grid;
    align-items: center;
    justify-items: center;
    grid-auto-flow: column dense;
    align-self:  end;
    width: fit-content;
}

.active-cog {
    transform: rotate(360deg);
    transition-duration: 0.5s;
    transition-timing-function: ease-out;
}

.active-reg {
    color: var(--pathogenic) !important;
    border: 2px var(--pathogenic) solid;
    border-radius: 8px;
}

.mtb-presentation-patient-data {
    display: flex;
    flex-direction: column;
    line-height: 60%;
    border: #d3d3d3 solid 1px;
    padding: 4px;
    padding-top: 8px;
    margin: 4px;
}

.mtb-presentation-patient-selection {
    border: #d3d3d3 solid 1px;
    padding: 4px;
    margin: 4px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: row;
    align-items: baseline;
}

.mtb-presentation-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 4px;
    margin: 4px;
    display: grid;
    grid-auto-flow: row dense;
    justify-items: center;
    align-items: center;
    border: #d3d3d3 solid 1px;
    column-gap: 8px;
}

.patient-dropdown {
    background-color: #d3d3d3;
    padding-left: 4px;
}

@media (max-width: 1366px) {

.mtb-presentation-patients {
    grid-template-columns: 3fr 2fr 2fr 0.5fr;
    font-size: 0.75rem;
    height: 100px;
}

.mtb-presentation-carousel {
    height: 40rem !important;
    font-size: 0.75rem;
}

.mtb-presentation-buttons {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    column-gap: 0;
}

.mtb-presentation-buttons > .v-btn {
    height: 80% !important;
    font-size: 0.75rem !important;
    padding: 1px 0 1px 0 !important;
}

.mtb-presentation-patient-selection > select {
    font-size: 1rem;
    height: 1.5rem;
}

.mtb-presentation-icons {
    column-gap: 0;
    max-width: fit-content;
}

.mtb-presentation-patient-data {
    font-size: 0.7rem;
    line-height: 1;
}

}

@media (max-width: 900px) {

.mtb-presentation-carousel {
    height: 35rem !important;
}

.mtb-presentation-patients {
    grid-template-columns: 3fr 1fr 2fr 0.5fr;
    column-gap: 8px;
}

.mtb-presentation-patient-data {
    max-width: 350px;
    line-height: 40%;
    overflow-x: scroll;
}

.mtb-presentation-patient-data > p {
    white-space: nowrap;
}

.mtb-presentation-patient-selection {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
}

.mtb-presentation-patient-selection > label {
    display: none;
}

.mtb-presentation-icons {
    grid-template-columns: 1fr;
    grid-auto-flow: row dense;
}

}

</style>

